import { useSessionFile } from '../../shared/hooks/session-file-hook';
import { useSessionState } from '../../shared/hooks/session-state-hook';
import { useProfileState } from '../../shared/hooks/profile-state-hook';
import { sessionKey } from '../../shared/util/session-storage';
import PageHeading from '../../shared/components/UIElements/PageHeading';
import ProfileAvatar from '../../shared/components/UIElements/ProfileAvatar';
import Icon from '../../shared/components/UIElements/Icon';
import EmptyState from '../../shared/components/UIElements/EmptyState';

import React, { useContext, useEffect, useState } from 'react';
import Card from '../../shared/components/UIElements/Card';
import { AuthContext } from '../../shared/Context/auth-context';
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner';
import Button from '../../shared/components/FormElements/Button';
import { useHttpClient } from '../../shared/hooks/http-hook';
import './DoctorProfile.css';
import { BACKEND_URL } from '../../shared/util/backend-url';

const DoctorProfile = () => {
  const auth = useContext(AuthContext);
  const { isLoading, error, sendRequest, clearError } = useHttpClient();

  const [editMode, setEditMode] = useSessionState(
    sessionKey(`doctor:${auth.doctorId}`, 'profile:editing'),
    false
  );
  const [doctorData, setDoctorData] = useProfileState(`doctor:${auth.doctorId}`, auth.doctor, [
    'name',
    'email',
    'specialization',
    'membercode',
    'associatedPharmacy',
    'signature'
  ]);
  const [signatureFile, setSignatureFile, signatureStatus] = useSessionFile(
    sessionKey(`doctor:${auth.doctorId}`, 'profile:signature')
  );
  const [signaturePreview, setSignaturePreview] = useState(null);
  useEffect(() => {
    if (!signatureFile) {
      setSignaturePreview(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSignaturePreview(reader.result);
    reader.readAsDataURL(signatureFile);
    return () => {
      if (reader.readyState === 1) reader.abort();
    };
  }, [signatureFile]);
  const [todaysPrescriptions, setTodaysPrescriptions] = useState([]);
  const [count, setCount] = useState(0);

  // Initialize doctor data from AuthContext
  useEffect(() => {
    if (auth.doctor && !doctorData) {
      setDoctorData(auth.doctor);
    }
  }, [auth.doctor, doctorData, setDoctorData]);

  // Fetch today's prescriptions
  useEffect(() => {
    const fetchPrescriptions = async () => {
      try {
        const response = await sendRequest(
          `${BACKEND_URL}/api/doctor/today/${doctorData._id}`,
          'GET',
          null,
          { Authorization: `Bearer ${auth.token}` }
        );
        setTodaysPrescriptions(response.data || []);
        setCount(response.count || 0);
      } catch (err) {
        console.error('Failed to fetch prescriptions:', err);
      }
    };

    if (doctorData?._id) {
      fetchPrescriptions();
    }
  }, [doctorData?._id, sendRequest, auth.token]);

  // Handle form input changes
  const inputChangedHandler = (event) => {
    setDoctorData({ ...doctorData, [event.target.name]: event.target.value });
  };

  // Handle digital signature file change
  const signatureChangeHandler = (event) => {
    const file = event.target.files[0];
    if (file) setSignatureFile(file);
  };

  // Submit updated profile
  const submitHandler = async (event) => {
    event.preventDefault();
    const formData = new FormData();
    formData.append('name', doctorData.name);
    formData.append('email', doctorData.email);
    formData.append('specialization', doctorData.specialization);
    formData.append('membercode', doctorData.membercode);
    formData.append('associatedPharmacy', doctorData.associatedPharmacy);
    if (signatureFile) {
      formData.append('signature', signatureFile);
    }

    try {
      const response = await sendRequest(
        `${BACKEND_URL}/api/doctor/${doctorData._id}`,
        'PATCH',
        formData,
        {
          Authorization: `Bearer ${auth.token}`
        }
      );
      const savedDoctor = response.updatedDoctor?._id
        ? response.updatedDoctor
        : response.doctor?._id
          ? response.doctor
          : doctorData;
      setDoctorData(savedDoctor);
      if (auth.updateDoctor) auth.updateDoctor(savedDoctor);
      setSignatureFile(null);
      setEditMode(false);
    } catch (err) {
      console.error('Update failed:', err);
    }
  };

  // Show loading spinner until doctor data is ready
  if (!doctorData) {
    return (
      <div className="doctor-profile">
        <Card className="doctor-card">
          <LoadingSpinner asOverlay />
        </Card>
      </div>
    );
  }

  return (
    <div className="doctor-profile">
      <PageHeading
        eyebrow="YOUR PRACTICE, AT A GLANCE"
        title={`Welcome back, ${doctorData.name}.`}
        description="A little clarity for your day. More time for your patients."
      >
        <Button to={`/${auth.doctorId}/search`}>
          <Icon name="search" size={18} /> Find a patient
        </Button>
      </PageHeading>
      <div className="doctor-summary">
        <div className="summary-primary">
          <span className="summary-icon">
            <Icon name="file" size={25} />
          </span>
          <div>
            <p>Prescriptions today</p>
            <strong>{isLoading ? '—' : count}</strong>
          </div>
          <span className="summary-date">
            <Icon name="calendar" size={16} />
            {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          </span>
        </div>
        <div className="summary-secondary">
          <span className="summary-icon">
            <Icon name="doctor" size={25} />
          </span>
          <div>
            <p>Your specialization</p>
            <strong>{doctorData.specialization || 'Not added yet'}</strong>
          </div>
        </div>
      </div>
      <div className="doctor-layout">
        <Card className="doctor-card">
          {isLoading && <LoadingSpinner asOverlay />}
          <div className="panel-heading">
            <h2>Practice profile</h2>
            <span className="badge">Doctor</span>
          </div>
          <div className="profile-identity">
            <ProfileAvatar
              name={doctorData.name}
              src={
                doctorData.profileImage
                  ? `${BACKEND_URL}/${doctorData.profileImage}`
                  : undefined
              }
              className="profile-avatar--large"
            />
            <div>
              <h2>{doctorData.name}</h2>
              <p>{doctorData.specialization || 'Your professional profile'}</p>
            </div>
          </div>
          {!editMode ? (
            <>
              <dl className="detail-list">
                <div>
                  <dt>Email address</dt>
                  <dd>{doctorData.email}</dd>
                </div>
                <div>
                  <dt>Doctor ID</dt>
                  <dd>{doctorData._id}</dd>
                </div>
                <div>
                  <dt>Member code</dt>
                  <dd>{doctorData.membercode}</dd>
                </div>
                <div>
                  <dt>Associated pharmacy</dt>
                  <dd>{doctorData.associatedPharmacy || 'Not added'}</dd>
                </div>
                <div>
                  <dt>Digital signature</dt>
                  <dd>
                    {doctorData.signature ? (
                      <img
                        src={`${BACKEND_URL}/${doctorData.signature}`}
                        alt="Signature"
                        style={{ maxWidth: '150px' }}
                      />
                    ) : (
                      'Not added'
                    )}
                  </dd>
                </div>
              </dl>
              <Button inverse onClick={() => setEditMode(true)}>
                Edit profile
              </Button>
            </>
          ) : (
            <form className="profile-form doctor-edit-form" onSubmit={submitHandler}>
              <button
                type="button"
                className="modal-back full-width"
                onClick={() => setEditMode(false)}
              >
                <Icon name="back" size={17} />
                Back to profile
              </button>
              {[
                ['name', 'Name'],
                ['email', 'Email address'],
                ['specialization', 'Specialization'],
                ['membercode', 'Member code'],
                ['associatedPharmacy', 'Associated pharmacy ID']
              ].map(([name, label]) => (
                <div key={name}>
                  <label htmlFor={`doctor-${name}`}>{label}</label>
                  <input
                    id={`doctor-${name}`}
                    type={name === 'email' ? 'email' : 'text'}
                    name={name}
                    value={doctorData[name] || ''}
                    onChange={inputChangedHandler}
                  />
                </div>
              ))}
              <div className="full-width">
                <label htmlFor="doctor-signature">Digital signature</label>
                <input
                  id="doctor-signature"
                  type="file"
                  accept="image/*"
                  onChange={signatureChangeHandler}
                />
                {signatureFile && (
                  <p className="session-save-note">
                    {signatureStatus === 'unavailable'
                      ? 'Reselect this signature after leaving the page; tab storage is full.'
                      : signatureStatus === 'saving'
                        ? 'Keeping signature in this tab…'
                        : `${signatureFile.name} selected`}
                  </p>
                )}
                {signaturePreview && (
                  <img src={signaturePreview} alt="Preview" style={{ maxWidth: '150px' }} />
                )}
              </div>
              <div className="action-row">
                <Button type="submit">Save changes</Button>
                <Button inverse type="button" onClick={() => setEditMode(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </Card>
        <Card className="today-card">
          <div className="panel-heading">
            <div>
              <h2>Today’s prescriptions</h2>
              <p>Your prescribing activity for today.</p>
            </div>
            <span className="badge">{count} total</span>
          </div>
          {error && (
            <div className="inline-error" role="alert">
              <p>{error}</p>
              <button type="button" onClick={clearError}>
                Dismiss
              </button>
            </div>
          )}
          {!isLoading && !error && todaysPrescriptions.length === 0 && (
            <EmptyState icon="file" title="A fresh start to your day">
              Your prescriptions will appear here as you create them.
            </EmptyState>
          )}
          <ul className="today-list">
            {Array.isArray(todaysPrescriptions) &&
              todaysPrescriptions.map((item, index) => {
                const time = new Date(item.createdAt).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                });
                return (
                  <li key={index}>
                    <ProfileAvatar name={item.patientName} />
                    <div className="today-list__patient">
                      <strong>{item.patientName}</strong>
                      <span>
                        {item.patientAge} yrs · {item.patientGender}
                      </span>
                      <p>{item.condition}</p>
                    </div>
                    <time>{time}</time>
                  </li>
                );
              })}
          </ul>
          <div className="today-footer">
            <Icon name="search" size={18} />
            <p>Ready for your next patient?</p>
            <Button inverse to={`/${auth.doctorId}/search`}>
              Find a patient <Icon name="arrow" size={16} />
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};
export default DoctorProfile;
